import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { Subject, filter, takeUntil } from 'rxjs';

import { GisPcvizService } from '@mirage-projects/cw-pcviz-core';

import { ExpressionConfig, ExpressionEvent, FieldExpressionItem } from '../expression-editor/expression-config';
import { ExpressionEditorService } from '../expression-editor/expression-editor.service';

@Component({
    selector: 'cw-input-expression',
    templateUrl: './input-expression.component.html',
    styleUrls: ['./input-expression.component.scss'],
    standalone: false,
})
/**
 * Component for displaying an expression input field with an expression editor button.
 */
export class InputExpressionComponent implements OnInit, OnDestroy {
    /**
     * Label text to be shown.
     */
    @Input() labelText = '';
    /**
     * Expression value.
     */
    @Input() expression = '';
    /**
     * Name of the field.
     */
    @Input() fieldName = '';
    /**
     * Input placeholder text.
     */
    @Input() placeholderText = '';
    /**
     * Editable flag.
     */
    @Input() isEditable = true;
    /**
     * List of fields with types to be used in the expression editor.
     */
    @Input() fieldsExpression: FieldExpressionItem[] = [];
    /**
     * If isValid is false the expression is invalid
     */
    @Input() isValid = true;
    /**
     * Output field with the Expression Event object.
     */
    @Output() expressionChange = new EventEmitter<ExpressionEvent>();

    private editorExpressionState: ExpressionConfig = new ExpressionConfig();
    private readonly onDestroy$ = new Subject<void>();

    constructor(
        private expressionEditorService: ExpressionEditorService,
        private gisPcvizService: GisPcvizService
    ) {}

    ngOnInit(): void {
        this.expressionEditorService.outputExpressionChange$
            .pipe(
                filter((expressionData) => expressionData.expressionState === this.editorExpressionState),
                takeUntil(this.onDestroy$)
            )
            .subscribe(() => {
                this.onChange(this.expressionEditorService.expressionString);
            });
        this.onChange();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    /**
     * Called when the expression changes.
     * @param expressionString The string representation of the expression.
     */
    onChange(expressionString?: string): void {
        if (expressionString) {
            this.expression = expressionString;
        }
        const parsedExpr = this.gisPcvizService.getExpression().stringToExpression(this.expression);
        if (this.expression.trim() !== '' && !parsedExpr) {
            this.isValid = false;
        } else {
            this.isValid = true;
        }

        this.expressionChange.emit({ expression: this.expression, expressionObject: parsedExpr, isValid: this.isValid });
    }

    /**
     * Shows the expression editor.
     */
    showExpressionEditor(): void {
        this.editorExpressionState.fieldsExpression = this.fieldsExpression;
        this.expressionEditorService.setExpressionState(this.editorExpressionState);
        this.expressionEditorService.setInputExpressionString(this.expression);
        this.expressionEditorService.showExpressionEditor();
    }
}
